import React, { useMemo, useState } from 'react';

import { STAGES } from '../../lib/constantes';
import { db } from '../../lib/firebase';
import { fmt } from '../../lib/formato';
import { computeSellerFunnel } from '../../lib/pipeline';

function SellerFunnelSection({ leads, sellers, onOpenLead, company }) {
  const data = useMemo(() => computeSellerFunnel(leads, sellers), [leads, sellers]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [syncing, setSyncing]   = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const andreSeller   = sellers.find(s => /andr[eé]/i.test(s.name));
  const leadsNoSeller = useMemo(() => leads.filter(l => !l.seller_id), [leads]);

  async function handleSyncAndre() {
    if (!andreSeller || leadsNoSeller.length === 0) return;
    setSyncing(true);
    try {
      await Promise.all(leadsNoSeller.map(l =>
        db.collection('companies').doc(company).collection('leads').doc(l.id).update({ seller_id: andreSeller.id })
      ));
      setSyncDone(true);
    } catch(e) {
      alert('Erro ao atribuir vendedor: ' + e.message);
    } finally {
      setSyncing(false);
    }
  }

  if (data.length === 0 || sellers.length === 0) return null;

  const maxTotal = Math.max(...data.map(d => d.total), 1);
  const barColor = r => r >= 50 ? 'text-green-600' : r >= 25 ? 'text-orange-500' : r > 0 ? 'text-red-500' : 'text-gray-300';

  const STAGE_LABEL = Object.fromEntries(STAGES.map(s => [s.key, s.label]));
  const TEMP_COLORS = { quente:'text-red-500', morno:'text-orange-400', frio:'text-blue-400', muito_frio:'text-gray-400' };
  const stageOrder  = ['backlog','prospeccao','em_contato','qualificacao','aguardando','proposta','criando_proposta','negociacao','enviar_contrato','aguardando_assinatura','fechado','perdido','repescagem'];

  function getSellerLeads(key) {
    return key === '__none__' ? leads.filter(l => !l.seller_id) : leads.filter(l => l.seller_id === key);
  }
  const modalLeads = selectedSeller
    ? [...getSellerLeads(selectedSeller.key)].sort((a,b) => stageOrder.indexOf(b.stage||'backlog') - stageOrder.indexOf(a.stage||'backlog'))
    : [];

  return (
    <div className="acard rounded-2xl p-5 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">👤 Pipeline por Vendedor</h3>
          <p className="text-xs text-gray-400 mt-0.5">Desempenho individual — leads, qualificação, proposta, fechamento e receita</p>
        </div>
        {andreSeller && leadsNoSeller.length > 0 && !syncDone && (
          <button
            onClick={handleSyncAndre}
            disabled={syncing}
            className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-3 py-2 hover:bg-amber-100 transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            {syncing ? '⏳ Atribuindo...' : `🔄 Atribuir ${leadsNoSeller.length} lead${leadsNoSeller.length>1?'s':''} sem vendedor ao André`}
          </button>
        )}
        {syncDone && (
          <span className="text-xs text-green-600 font-medium">✅ {leadsNoSeller.length} leads atribuídos ao André</span>
        )}
      </div>

      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs min-w-[600px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="pb-2 text-left text-gray-400 font-medium pl-1 w-36">Vendedor</th>
              <th className="pb-2 text-center text-gray-400 font-medium">Leads</th>
              <th className="pb-2 text-center text-gray-400 font-medium">Qualif.</th>
              <th className="pb-2 text-center text-gray-400 font-medium">Proposta</th>
              <th className="pb-2 text-center text-gray-400 font-medium">Fechados</th>
              <th className="pb-2 text-center text-gray-400 font-medium">Win Rate</th>
              <th className="pb-2 text-right text-gray-400 font-medium pr-1">Pipeline</th>
              <th className="pb-2 text-right text-gray-400 font-medium pr-1">Receita</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.map(d => (
              <tr key={d.key} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={()=>setSelectedSeller(d)}>
                <td className="py-2.5 pl-1 pr-3">
                  <span className="font-medium text-gray-800 whitespace-nowrap">👤 {d.label}</span>
                  <span className="ml-1 text-gray-300 t-caption-2">↗</span>
                </td>
                <td className="py-2.5 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-bold text-gray-800">{d.total}</span>
                    <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-400 rounded-full" style={{width:`${(d.total/maxTotal)*100}%`}} />
                    </div>
                  </div>
                </td>
                <td className="py-2.5 text-center">
                  <div className="flex flex-col items-center leading-tight">
                    <span className="font-semibold text-gray-700">{d.qualifOrAbove}</span>
                    <span className="text-gray-400">{d.qualifRate.toFixed(0)}%</span>
                  </div>
                </td>
                <td className="py-2.5 text-center">
                  <div className="flex flex-col items-center leading-tight">
                    <span className="font-semibold text-gray-700">{d.propOrAbove}</span>
                    <span className="text-gray-400">{d.propRate.toFixed(0)}%</span>
                  </div>
                </td>
                <td className="py-2.5 text-center">
                  <span className={`font-bold text-sm ${d.fechado > 0 ? 'text-green-600' : 'text-gray-300'}`}>{d.fechado}</span>
                </td>
                <td className="py-2.5 text-center">
                  <span className={`font-bold ${barColor(d.winRate)}`}>
                    {d.winRate > 0 ? `${d.winRate.toFixed(0)}%` : '—'}
                  </span>
                </td>
                <td className="py-2.5 text-right pr-1">
                  <span className={`font-medium ${d.pipelineValue > 0 ? 'text-indigo-700' : 'text-gray-300'}`}>
                    {d.pipelineValue > 0 ? fmt(d.pipelineValue) : '—'}
                  </span>
                </td>
                <td className="py-2.5 text-right pr-1">
                  <span className={`font-bold ${d.closedValue > 0 ? 'text-green-700' : 'text-gray-300'}`}>
                    {d.closedValue > 0 ? fmt(d.closedContractValue > d.closedValue ? d.closedContractValue : d.closedValue) : '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* C-32: metas */}
      {data.some(d => d.meta > 0) && <div className="border-t border-gray-100 pt-4 space-y-3">
        <p className="text-xs font-medium text-gray-400">Meta de receita fechada</p>
        {data.filter(d => d.meta > 0).map(d => {
          const atingido = d.closedValue;
          const pct   = (atingido / d.meta) * 100;
          const barra = Math.min(100, pct);
          const cor   = pct >= 100 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-400' : 'bg-red-400';
          return (
            <div key={d.key}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <span className="text-xs font-medium text-gray-700 truncate">{d.label}</span>
                <span className="t-caption-2 text-gray-400 whitespace-nowrap">
                  {fmt(atingido)} de {fmt(d.meta)}
                  <span className={`ml-1.5 font-semibold ${pct >= 100 ? 'text-green-600' : 'text-gray-500'}`}>
                    {pct.toFixed(0)}%
                  </span>
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${cor}`} style={{width:`${barra}%`}} />
              </div>
              <p className="t-caption-2 text-gray-400 mt-0.5">
                {pct >= 100
                  ? `Meta batida, ${fmt(atingido - d.meta)} acima`
                  : `Faltam ${fmt(d.meta - atingido)}`}
              </p>
            </div>
          );
        })}
      </div>}

      {/* Barras visuais */}
      <div className="border-t border-gray-100 pt-4 space-y-3">
        <p className="text-xs font-medium text-gray-400">Funil visual por vendedor</p>
        {data.map(d => (
          <div key={d.key}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-700">👤 {d.label}</span>
              <span className="text-xs text-gray-400">{d.fechado > 0 ? `${d.fechado} fechado${d.fechado>1?'s':''}` : 'nenhum fechado'}</span>
            </div>
            <div className="flex h-4 rounded overflow-hidden bg-gray-100 w-full">
              {d.total > 0 && <>
                <div className="bg-indigo-200 flex items-center justify-center t-caption-2 text-indigo-700 font-semibold" style={{flex:d.total}} title={`${d.total} leads`}>{d.total}</div>
                {d.qualifOrAbove > 0 && <div className="bg-indigo-400 flex items-center justify-center t-caption-2 text-white font-semibold" style={{flex:d.qualifOrAbove}} title={`${d.qualifOrAbove} qualif.`}>{d.qualifOrAbove > 1 ? d.qualifOrAbove : ''}</div>}
                {d.propOrAbove > 0  && <div className="bg-purple-400 flex items-center justify-center t-caption-2 text-white font-semibold" style={{flex:d.propOrAbove}} title={`${d.propOrAbove} proposta`}>{d.propOrAbove > 1 ? d.propOrAbove : ''}</div>}
                {d.fechado > 0      && <div className="bg-green-500 flex items-center justify-center t-caption-2 text-white font-semibold" style={{flex:d.fechado}} title={`${d.fechado} fechados`}>{d.fechado}</div>}
              </>}
            </div>
          </div>
        ))}
        <div className="flex flex-wrap gap-4 pt-1">
          {[['bg-indigo-200','Entrada'],['bg-indigo-400','Qualificou'],['bg-purple-400','Proposta'],['bg-green-500','Fechou']].map(([bg,lb])=>(
            <span key={lb} className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className={`w-3 h-3 rounded ${bg} inline-block`}/>{lb}
            </span>
          ))}
        </div>
      </div>

      {/* Modal drill-down */}
      {selectedSeller && (
        <div className="glass-overlay fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={()=>setSelectedSeller(null)}>
          <div className="animate-sheet acard rounded-3xl shadow-2xl mt-8 mb-10 w-full max-w-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
              <div>
                <h2 className="t-subhead font-semibold text-gray-900 tracking-tight">
                  👤 {selectedSeller.label}
                  <span className="ml-2 text-sm font-normal text-gray-400">{modalLeads.length} leads</span>
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Clique em um lead para abrir os detalhes</p>
              </div>
              <button aria-label="Fechar" onClick={()=>setSelectedSeller(null)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-bold">✕</button>
            </div>
            <div className="px-6 py-5 space-y-2 max-h-[70vh] overflow-y-auto">
              {modalLeads.length === 0
                ? <p className="text-sm text-gray-400 text-center py-8">Nenhum lead</p>
                : modalLeads.map(l => {
                    const isClosed = l.stage==='fechado', isLost = l.stage==='perdido';
                    return (
                      <div key={l.id} onClick={()=>{ setSelectedSeller(null); onOpenLead(l); }}
                        className="flex items-center justify-between gap-3 bg-black/[0.03] hover:bg-black/[0.06] rounded-2xl px-4 py-3 cursor-pointer transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{l.company_name||'—'}</p>
                          {l.contact_name && <p className="text-xs text-gray-400 truncate">{l.contact_name}</p>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {l.temperature && <span className="text-xs">{l.temperature==='quente'?'🔥':l.temperature==='morno'?'🌤️':l.temperature==='frio'?'🌨️':'🧊'}</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${isClosed?'bg-green-100 text-green-700':isLost?'bg-red-100 text-red-600':'bg-indigo-50 text-indigo-600'}`}>
                            {STAGE_LABEL[l.stage]||'Backlog'}
                          </span>
                          {l.value > 0 && <span className="text-xs font-bold text-gray-700 whitespace-nowrap">{fmt(Number(l.value))}</span>}
                        </div>
                      </div>
                    );
                  })
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KPIsView ─────────────────────────────────────────────

export { SellerFunnelSection };
